// src/utils/colors.ts

/**
 * Palette de couleurs World Of Numeric
 * Centralisée pour une utilisation cohérente dans toute l'application
 * 
 * IMPORTANT : Ces couleurs sont synchronisées avec les variables CSS dans src/styles/variables.css
 * Toute modification ici devrait être répercutée dans ce fichier CSS
 */
export const themeColors = {
  // Couleurs principales
  'won-pink': '#ed4c67',
  'won-blue': '#3498db',
  'won-light': '#ecf0f1',
  'won-dark': '#34495e',
  'won-black': '#131313',

  // Couleurs intermédiaires
  'won-teal': '#1abc9c',     // Utilisé pour les icônes/boutons de succès
  'won-emerald': '#2ecc71',  // Utilisé pour les indicateurs positifs
  'won-orange': '#e67e22',   // Utilisé pour les avertissements/éléments en avant
  'won-red': '#e74c3c',      // Utilisé pour les erreurs/alertes

  // Nuances intermédiaires
  'won-blue-dark': '#2980b9',  // Version plus foncée du bleu principal
  'won-pink-light': '#f19cb7', // Version plus claire du rose vif
  'won-bluegray': '#516a80',   // Pour les éléments secondaires
  'won-gray': '#95a5a6',       // Pour les textes secondaires ou désactivés

  // Nuances neutres
  'won-darkgray': '#7f8c8d',   // Pour certains textes/éléments d'interface
  'won-lightgray': '#bdc3c7',  // Pour les bordures ou séparateurs
};

/**
 * Alias des couleurs principales pour un accès direct
 * Cette interface permet d'accéder rapidement aux couleurs les plus utilisées
 */
export const colors = {
  primary: themeColors['won-pink'],
  secondary: themeColors['won-blue'],
  success: themeColors['won-emerald'],
  warning: themeColors['won-orange'],
  error: themeColors['won-red'],
  dark: themeColors['won-dark'],
  light: themeColors['won-light'],
  text: themeColors['won-black'],
  grayText: themeColors['won-darkgray'],
  border: themeColors['won-lightgray'],
  background: themeColors['won-light'],
  // Aliases pour compatibilité avec le code existant
  pink: themeColors['won-pink'],
  blue: themeColors['won-blue'],
  emerald: themeColors['won-emerald'],
  teal: themeColors['won-teal'],
  orange: themeColors['won-orange'],
  red: themeColors['won-red'],
};
/**
 * Génère un dégradé CSS entre deux couleurs
 */
export function createGradient(from: string, to: string, direction: string = "to right"): string {
  return `linear-gradient(${direction}, ${from}, ${to})`;
}

/**
 * Ajoute une transparence à une couleur (format hex -> rgba)
 */
export function withOpacity(colorValue: string, opacity: number): string {
  // Si la couleur est au format hexadécimal
  if (colorValue.startsWith('#')) {
    // Convertir hex en rgb
    const r = parseInt(colorValue.slice(1, 3), 16);
    const g = parseInt(colorValue.slice(3, 5), 16);
    const b = parseInt(colorValue.slice(5, 7), 16);

    // Retourner avec l'opacité spécifiée
    return `rgba(${r}, ${g}, ${b}, ${opacity})`;
  }

  // Si la couleur est déjà au format rgb
  if (colorValue.startsWith('rgb(')) {
    // Extraire les valeurs RGB
    const rgbValues = colorValue.match(/\d+/g);
    if (rgbValues && rgbValues.length === 3) {
      return `rgba(${rgbValues[0]}, ${rgbValues[1]}, ${rgbValues[2]}, ${opacity})`;
    }
  }

  // Pour les couleurs déjà au format rgba, remplacer simplement la valeur d'opacité
  if (colorValue.startsWith('rgba(')) {
    return colorValue.replace(/[\d.]+\)$/, `${opacity})`);
  }

  // Par défaut, retourner la couleur inchangée
  return colorValue;
}

/**
 * Crée un dégradé transparent (pour les effets de fond)
 */
export function createTransparentGradient(
  from: string,
  to: string,
  fromOpacity: number = 0.2,
  toOpacity: number = 0.1,
  direction: string = "to right"
): string {
  return `linear-gradient(${direction}, ${withOpacity(from, fromOpacity)}, ${withOpacity(to, toOpacity)})`;
}
